/**
 * Section constants for Lab Investigations v2. Tests come from
 * api-lab-investigation-v2-service's federated `/labtest-catalog` (which
 * proxies api-labtest-service). This file holds the flag band lookups used
 * by the table + trend modal.
 */

/**
 * Flag bands. `key` matches the backend enum. `unknown` renders without a
 * chip in the table (missing numeric or ref range).
 */
export const FLAG_BANDS = {
  normal:   { key: "normal",   label: "Normal",   bg: "#e8f7ef", fg: "#1b7f4c", dot: "#1b7f4c" },
  low:      { key: "low",      label: "Low",      bg: "#e6f0fa", fg: "#1d4ed8", dot: "#2563eb" },
  medium:   { key: "medium",   label: "Medium",   bg: "#fef7d6", fg: "#a16207", dot: "#ca8a04" },
  high:     { key: "high",     label: "High",     bg: "#fdeaea", fg: "#b91c1c", dot: "#b91c1c" },
  abnormal: { key: "abnormal", label: "Abnormal", bg: "#fef1e9", fg: "#c2410c", dot: "#ea580c" },
  none:     { key: "none",     label: "None",     bg: "#f4f5f7", fg: "#6a7282", dot: "#8a94a6" },
  unknown:  { key: "unknown",  label: "—",        bg: "#f4f5f7", fg: "#6a7282", dot: "#8a94a6" },
};

export const flagBand = (key) => FLAG_BANDS[key] || FLAG_BANDS.unknown;

/**
 * A reading's tag: the qualifier the capturing caller puts on readings that
 * share one test but mean different things ("Morning systolic" / "Evening
 * systolic", "PP1"–"PP3"). api-lab-investigation-v2-service keeps them all in
 * the ONE investigation and stores the tag on every test snapshot — each
 * statusHistory entry's `test` has its own, and the investigation's top-level
 * `test` has the tag of whichever reading was written last. Catalogue picks
 * have none, so "" is the common case and must render as nothing at all.
 */
export const readingTag = (test) =>
  (test && typeof test.tag === "string" && test.tag.trim()) || "";

/** The test's name qualified by its reading's tag: "Glucose, Post Prandial · PP2". */
export const nameWithTag = (test, fallback = "") => {
  const name = (test && test.name) || fallback;
  const tag = readingTag(test);
  if (!tag) return name;
  return name ? `${name} · ${tag}` : tag;
};

/**
 * Vertical ordering for the "Range over time" chart's categorical Y axis.
 * Top of the chart is `High` (most severe on the high end); `None`
 * anchors the bottom. Matches the design mock.
 */
export const RANGE_AXIS = ["high", "medium", "normal", "low", "abnormal", "none"];

/**
 * Chart-only rail backgrounds. Kept SEPARATE from `FLAG_BANDS.bg` so the
 * pale trend-rail palette doesn't leak into the section table's Range
 * chip or the audit table's Range pill, which use a saturated variant
 * of the same hue for chip contrast against the row background.
 *
 * Colour codes locked from the trend-chart mock. Rendered at
 * `opacity: 1` in RangeChart so the pale values land pixel-accurate.
 */
export const FLAG_CHART_BG = {
  high:     "#FEEEEE",
  medium:   "#FEF8DD",
  normal:   "#EAFDF1",
  low:      "#E9F2FE",
  abnormal: "#FFF4E6",
  none:     "#F8F8FA",
  unknown:  "#F8F8FA",
};
